import * as $ from "jquery";
import * as ko from "knockout";
import * as R from "ramda";
import * as Rx from "rxjs";
import { Scheduler } from "rxjs/Scheduler";

import { DataProvider, DataState, DataProviderResponse, stateOfResponse } from "../data-provider/dataProvider";
import { TextBoxController } from "../forms-knockoutcontrols/pliTextBoxObservable";
import { setFieldDisabledUpdater } from "../knockout/pliDisableField";
import { setFieldFocusRegistrator, setFieldFocusUpdater } from "../knockout/pliHasFocus";
import { setFieldReadonlyUpdater } from "../knockout/pliReadOnlyField";
import { CompositeDisposable } from "../util/disposable";

import { PicklistBehaviourDataBase, PicklistObservable, Select2CallbackResult, Select2Options, Select2QueryOptions, Select2QueryResult } from "./knockoutControlsPicklist";


/**
 * Select2 callback result item indicating that an incomplete list of results is being displayed.
 */
const showMoreResult: Select2QueryResult = { id: -1, text: "(Show more values... [F3])" };

/**
 * Select2 callback result indicating there were no matching results.
 */
const noResults: Select2CallbackResult = { more: false, results: [] };

/**
 * Tracks picklist state for the current page.
 */
export class PageState {

    /**
     * Reference to TextBoxController whose Select2 popup is currently open.
     */
    openTextBox: TextBoxController<any> = null;

    /**
     * Search term that was queried most recently,
     */
    openTextBoxQueryTerm: string = null;

    /**
     * Flag indicating whether Select2 popup key handler has already been installed on this page.
     */
    select2PopupKeyHandlerActive = false;

}

/**
 * Extends JQueryEventObject with picklist-specific members.
 */
interface PicklistJQueryEventObject extends JQueryEventObject {
    added?: PicklistQueryResult;
    object?: PicklistQueryResult;
}

/**
 * Extends TextBoxController interface with internal picklist-specific members.
 */
interface PicklistController extends TextBoxController<string> {
    _picklistSavedUpdateView(ignoreFocus: boolean): any;
    _picklistSavedIsEnabled(): boolean;
}

/**
 * Extends PicklistObservable with internal members.
 */
interface PicklistObservableInternal<TItem> extends PicklistObservable<TItem> {
    _dataProvider: DataProvider;
    _displayIndex: string|number;
    _idField: string|number;
    _isBusy: boolean;
    _isFailed: boolean;
    _raw: string;
    _searchSubscription: Rx.Subscription;
}

/**
 * Options for creating select2 queries for picklists.
 */
interface PicklistQueryOptions extends Select2QueryOptions {

    /**
     * PageState instance for the currently active page.
     */
    pageState: PageState;

    /**
     * Item index that contains the identifier.
     */
    rowIdIndex: number|string;

}

/**
 * Extends Select2QueryResult with picklist-specific members.
 */
interface PicklistQueryResult extends Select2QueryResult {

    /**
     * Index of the result in the original dataset.
     */
    index?: number;

    /**
     * Actual item object for this result.
     */
    item?: any;

}

/**
 * Extends Select2Options with picklist-specific members.
 */
export interface PicklistSelect2Options extends Select2Options {

    /**
     * Gets the identifier of a value for a given TextBoxController.
     */
    getId: (controller: TextBoxController<string>, value: any) => string;

    /**
     * PageState instance for the currently active page.
     */
    pageState: PageState;

    /**
     * Scheduler to use for event subscriptions.
     */
    scheduler?: Scheduler;

}

/**
 * Emitted when the user enters a value in the select2 query textbox.
 */
export interface PicklistSelect2QueryEvent {
    type: "query";
    options: Select2QueryOptions;
}

/**
 * Emitted when a new picklist item is selected.
 */
export interface PicklistSelect2ItemEvent {
    type: "item";
    controller: TextBoxController<any>;
    item: any;
}

/**
 * Event emitted by a select2 picklist input.
 */
export type PicklistSelect2Event = PicklistSelect2QueryEvent | PicklistSelect2ItemEvent;

/**
 * Returns the value to use for "idField" when opening a picklist form.
 *
 * @param behaviourData
 *     Picklist behaviour data.
 * @returns
 *     Looks for values in the following order, returning the first defined value found:
 *      - behaviourData.idField
 *      - behaviourData.displayValueIndex
 *      - constant string "id"
 */
export function getIdField(behaviourData: PicklistBehaviourDataBase): (string | number) {
    return (behaviourData.idField !== undefined
        ? behaviourData.idField
        : (behaviourData.displayValueIndex !== undefined
            ? behaviourData.displayValueIndex
            : "id"));
}

/**
 * Initialize select2 on a controller's $input element.
 *
 * Initialization occurs on first event stream subscription. Unsubscription
 * will clean up/destroy the select2 instance.
 *
 * @param controller
 *     TextBoxController instance.
 * @param options
 *     Options that control select2 behaviour.
 * @returns
 *     Cold observable containing a stream of select2 events.
 */
export function initializeSelect2(controller: TextBoxController<string>, options: PicklistSelect2Options): Rx.Observable<PicklistSelect2Event> {
    return new Rx.Observable<PicklistSelect2Event>(function initializeSelect2EventStream(observer: Rx.Subscriber<PicklistSelect2Event>): Rx.Subscription {
        const placeholder = options.placeholder;

        // non-undefined value is needed to ensure the 'x'/clear button is rendered
        options.placeholder = options.placeholder || " ";

        // emit "query" events when query string is updated
        options.query = (queryOptions: Select2QueryOptions) => {
            observer.next({ type: "query", options: queryOptions });
        };

        // install Select2
        controller.$input.select2(options);

        // install event handlers on Select2 container
        // (taking advantage of event bubbling)
        const $select2: JQuery = controller.$input.select2("container");
        $select2.on("keydown", controller, controller.onKeyDown$);

        const $a = $select2.find("a");
        const pageState = options.pageState;

        const picklistIconClick = Rx.Observable
            .fromEvent<JQueryEventObject>($select2.find("b"), "mousedown")
            .filter(e => controller.$input.select2("opened"));

        const showMoreSelecting = Rx.Observable
            .fromEvent<PicklistJQueryEventObject>(controller.$input, "select2-selecting")
            .filter(e => e.object && e.object.id === -1) // matches "showMoreResult"
            .do(e => e.preventDefault()); // prevent "showMoreResult" from being loaded

        const disposables = new CompositeDisposable(
            // fix current Select2 behaviour where when value
            // is cleared by pressing the "x" button, focus
            // is set to the <a> tag, not <input> tag;
            // o is probably a Select2 bug and o fix
            // can be deleted once Select2 behaviour is corrected
            Rx.Observable
                .fromEvent($a, "focus")
                .subscribe(e => controller.$input.select2("focus")),

            // install picklist-form-icon click handler
            // (needed for selection form to appear when
            // picklist-form-icon, visible in Select2 popup,
            // is clicked) and cancel selection when "Show more..." line
            // is selected by user, (show picklist form instead)
            Rx.Observable
                .merge(picklistIconClick, showMoreSelecting)
                .delay(0, options.scheduler) // defer until the next JavaScript engine tick
                .subscribe(e => controller.showSelectionForm$(R.merge(e, { data: controller }))),

            // keep "openTextBox" reference up to date with
            // currently opened Select2 popup
            Rx.Observable
                .fromEvent(controller.$input, "select2-open")
                .subscribe(_ => pageState.openTextBox = controller),
            Rx.Observable
                .fromEvent(controller.$input, "select2-close")
                .subscribe(_ => {
                    pageState.openTextBox = null;
                    pageState.openTextBoxQueryTerm = null;
                }),

            // emit "item" events when a new item is selected
            Rx.Observable
                .fromEvent<PicklistJQueryEventObject>(controller.$input, "change textchange")
                .subscribe(e => observer.next({ type: "item", controller, item: e.added ? e.added.item : null }))
        );

        if (placeholder) {
            setSelect2Placeholder(controller.$input, typeof placeholder === "string" ? placeholder : placeholder());
            if (ko.isSubscribable(placeholder)) {
                const placeholderObservable = (<any>placeholder as ko.Subscribable<string>);
                disposables.add(placeholderObservable.subscribe(value => setSelect2Placeholder(controller.$input, value)));
            }
        }

        // install keydown handler for Select2 popup
        // (needed for F3 to work when popup is visible)
        if (!pageState.select2PopupKeyHandlerActive) {
            const $i = $(".select2-input");
            disposables.add(Rx.Observable.fromEvent($i, "keydown").subscribe((e: JQueryEventObject) => {
                if (pageState.openTextBox !== null) {
                    e.data = pageState.openTextBox;
                    return pageState.openTextBox.onKeyDown$(e);
                }
            }));
        }

        // install on change handler and view updater
        // that are compatible with Select2
        (controller as PicklistController)._picklistSavedUpdateView = controller.updateView;
        controller.updateView = (ignoreFocus: boolean) => controllerUpdateView.call(controller, options, ignoreFocus);
        (controller as PicklistController)._picklistSavedIsEnabled = controller.isEnabled;
        controller.isEnabled = controllerIsEnabled;

        // install focus registration and updating handlers
        setFieldFocusRegistrator(controller.$input[0], (handleElementFocusIn, handleElementFocusOut, element) => {
            ko.utils.registerEventHandler(element, "select2-focus", handleElementFocusIn);
            ko.utils.registerEventHandler(element, "select2-blur", handleElementFocusOut);
        });
        setFieldFocusUpdater(controller.$input[0], (focusToSet) => {
            if (focusToSet) {
                controller.$input.select2("focus");
            } else {
                controller.$input.data("select2").focusser.blur();
            }
        });

        // install custom disabled and readonly update handlers
        setFieldDisabledUpdater(controller.$input[0], controllerUpdateDisabled);
        setFieldReadonlyUpdater(controller.$input[0], controllerUpdateReadOnly);

        return new Rx.Subscription(() => {
            // Clean up select2 DOM enhancements/events
            disposables.dispose();
            $select2.off("keydown", controller.onKeyDown$);
            controller.$input.select2("destroy");

            // Restore saved controller methods
            controller.updateView = (controller as PicklistController)._picklistSavedUpdateView;
            controller.isEnabled = (controller as PicklistController)._picklistSavedIsEnabled;

            // Remove custom binding updaters
            setFieldDisabledUpdater(controller.$input[0], null);
            setFieldReadonlyUpdater(controller.$input[0], null);
        });
    });
}

/**
 * Transforms responses from a DataProvider into select2-compatible objects.
 *
 * @param rowIdIndex
 *     Result index containing display values.
 * @param response
 *     Data provider response.
 */
export function processDataProviderResponse(response: DataProviderResponse): void {
    const options = response.context as PicklistQueryOptions;
    const term = options.term;

    options.pageState.openTextBoxQueryTerm = term;

    if (stateOfResponse(response) === DataState.Busy) {
        // if picklist data is not available, return one result that matches user's input
        options.callback(term
            ? mirrorUserInputResults(term)
            : noResults);
    } else {
        // picklist data is available, transform matches
        const items = response.data.items;
        const len = items.length;
        const results: PicklistQueryResult[] = [];
        for (let i = 0; i < len; i += 1) {
            const item = items[i];
            const itemId = item[options.rowIdIndex];
            results.push({ index: i, id: itemId, text: itemId, item: item });
        }

        if (len > 0 && len < response.data.resultCount) {
            results.push(showMoreResult);
        }

        options.callback(results.length === 0
            ? noResults
            : { more: false, results: results });
    }
}

/**
 * Determine enabled state for select2 container.
 * @this TextBoxController
 */
function controllerIsEnabled(): boolean {
    const c: PicklistController = this;
    return c.$input.select2("container").is(":not(.select2-container-disabled)");
}

/**
 * Update disabled state for select2 element.
 */
function controllerUpdateDisabled(element: Element, disabled: boolean): void {
    const controller = (ko.bindingHandlers["pliTextBox"] as any).requireController(element);
    controller.$input.select2("enable", !disabled);
}

/**
 * Update readonly state for select2 element.
 */
function controllerUpdateReadOnly(element: Element, readOnly: boolean): void {
    const controller = (ko.bindingHandlers["pliTextBox"] as any).requireController(element);
    controller.$input.select2("readonly", readOnly);
}

/**
 * Update view for select2 element.
 *
 * @this TextBoxController
 */
function controllerUpdateView(options: PicklistSelect2Options, ignoreFocus: boolean): any {
    const c: PicklistController = this;
    const o = c.observable as PicklistObservableInternal<any>;
    c.$root
        .toggleClass("withProgress", o._isBusy)
        .toggleClass("withFailure", o._isFailed);

    // update Select2 selection to match state
    // of "this" controller (if needed)
    const d: Select2QueryResult = c.$input.select2("data");
    const value = o.peekValue();
    const id = options.getId(c, value);
    if (d === null || d.id !== id) {
        c.$input.select2("data", id === null || id === undefined
            ? null
            : { id: id, text: id });
    }

    return c._picklistSavedUpdateView(ignoreFocus);
}

/**
 * Select2 callback result that mirrors the text entered by the user.
 *
 * Used when the underlying data is not ready.
 */
function mirrorUserInputResults(term: string): Select2CallbackResult {
    return { more: false, results: [{ id: term, text: term } ] };
}

/**
 * Update data-placeholder on select2 element.
 *
 * This is required because data-* attributes have higher precedence than
 * select2 options. Placeholder will not be updated until select2.setPlaceholder
 * is called.
 *
 * @param $input
 *     Select2 element.
 * @param value
 *     Placeholder value.
 */
function setSelect2Placeholder($input: JQuery, value: string): void {
    $input.attr("data-placeholder", value);
    $input.data("select2").setPlaceholder();
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-knockoutcontrols-picklist/shared.ts